import { computed } from "vue";
import { toValue } from "@erp/shared/utils/to-value";
import { useLoginInfo } from "../useLoginInfo";
/**
 * @description 승인 관련 칩 상태 및 권한 체크에 도움을 주는 훅입니다.
 *
 * @param {Object} nowApproval - 현재 승인 상태
 * @param {number[]} canCheckMember - 확인 가능한 사용자 ID 목록
 * @param {number[]} canApproveMember - 승인 가능한 사용자 ID 목록
 * @param {Object[]} readonlyPhase - 칩 선택이 불가한 승인 상태 목록
 *
 * @returns
 * - canApprove: 현재 로그인한 사용자가 승인 가능한지 여부를 반환하는 함수
 * - isPhaseEqual: 현재 승인 상태와 주어진 승인 상태가 같은지 여부를 반환하는 함수
 */
export function useApprovalChip({ nowApproval, canCheckMember, canApproveMember, readonlyPhase, }) {
    const canCheckMemberValue = toValue(canCheckMember);
    const canApproveMemberValue = toValue(canApproveMember);
    const readonlyPhaseValue = toValue(readonlyPhase);
    const nowApprovalValue = toValue(nowApproval);
    const { id } = useLoginInfo();
    // 수정 가능한 사용자인지
    const isAccessibleUser = computed(() => {
        return canApproveMemberValue.value.includes(id.value);
    });
    // readonly 단계인지
    const isReadonlyPhase = computed(() => {
        return readonlyPhaseValue.value.includes(nowApprovalValue.value);
    });
    // 확인 가능한지
    const canCheck = computed(() => {
        return (!isReadonlyPhase.value && canCheckMemberValue?.value?.includes(id.value));
    });
    // 승인 가능한지
    const canApprove = computed(() => isAccessibleUser.value && !isReadonlyPhase.value);
    const approveMessage = computed(() => {
        if (!isAccessibleUser.value) {
            return "승인자만 변경 가능합니다.";
        }
        if (isReadonlyPhase.value) {
            return `${readonlyPhaseValue.value.join(", ")} 단계에서는 변경할 수 없습니다.`;
        }
        return "";
    });
    const isPhaseEqual = (approvalPhase) => {
        return nowApprovalValue.value === approvalPhase;
    };
    return {
        canApprove,
        canCheck,
        approveMessage,
        isAccessibleUser,
        isReadonlyPhase,
        isPhaseEqual,
    };
}
